import { filterSessionsByCourse } from '../../utils/sessionUtils';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Bell, Send, Trash2, Loader2, Users, BookOpen, User, AlertCircle, CheckCircle, GraduationCap, Briefcase } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SendNotification() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [activeTab, setActiveTab] = useState('students'); // 'students' or 'staff'
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'general',
    priority: 'normal',
    target_type: 'all',
    target_course_id: '',
    target_session_id: '',
    target_student_ids: [],
    target_staff_ids: []
  });

  useEffect(() => {
    fetchNotifications();
    fetchCourses();
    fetchSessions();
    fetchStudents();
    fetchStaff();
  }, []);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/api/notifications/');
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await apiClient.get('/api/master-settings/courses');
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await apiClient.get('/api/master-settings/sessions');
      setSessions(response.data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await apiClient.get('/api/students/dropdown');
      setStudents(response.data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await apiClient.get('/api/staff/');
      setStaffList(response.data || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    
    setIsSending(true);
    
    try {
      await apiClient.post('/api/notifications/send', formData);
      toast.success('Notification sent successfully!');
      setFormData({
        title: '',
        message: '',
        type: 'general',
        priority: 'normal',
        target_type: activeTab === 'staff' ? 'all_staff' : 'all',
        target_course_id: '',
        target_session_id: '',
        target_student_ids: [],
        target_staff_ids: []
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification');
    } finally {
      setIsSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    
    try {
      await apiClient.delete(`/api/notifications/${id}`);
      toast.success('Notification deleted');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const generateFeeReminders = async () => {
    try {
      const response = await apiClient.post('/api/notifications/generate-fee-reminders');
      toast.success(`${response.data.reminders_sent} fee reminders generated!`);
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to generate fee reminders');
    }
  };

  const getTargetIcon = (targetType) => {
    switch (targetType) {
      case 'all': return <Users className="w-4 h-4" />;
      case 'all_staff': return <Briefcase className="w-4 h-4" />;
      case 'all_students': return <GraduationCap className="w-4 h-4" />;
      case 'course': return <BookOpen className="w-4 h-4" />;
      case 'individual': return <User className="w-4 h-4" />;
      case 'staff_individual': return <User className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getTargetLabel = (targetType) => {
    switch (targetType) {
      case 'all': return 'All Students';
      case 'all_staff': return 'All Staff';
      case 'all_students': return 'All Students';
      case 'course': return 'Course';
      case 'individual': return 'Individual Student';
      case 'staff_individual': return 'Individual Staff';
      default: return 'All';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">High</span>;
      case 'low':
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">Low</span>;
      default:
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Normal</span>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="w-6 h-6" /> Send Notification
        </h1>
        <Button variant="outline" onClick={generateFeeReminders}>
          <AlertCircle className="w-4 h-4 mr-2" />
          Generate Fee Reminders
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Send Form */}
        <Card>
          <CardHeader>
            <CardTitle>New Notification</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Tab Selection */}
            <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => {
                  setActiveTab('students');
                  setFormData({...formData, target_type: 'all', target_staff_ids: []});
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md transition-colors ${
                  activeTab === 'students' ? 'bg-white shadow text-green-700' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <GraduationCap className="w-4 h-4" />
                To Students
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('staff');
                  setFormData({...formData, target_type: 'all_staff', target_student_ids: [], target_course_id: ''});
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md transition-colors ${
                  activeTab === 'staff' ? 'bg-white shadow text-indigo-700' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Briefcase className="w-4 h-4" />
                To Staff
              </button>
            </div>

            <form onSubmit={handleSend} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Notification title"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Notification message"
                  rows={4}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="general">General</option>
                    <option value="announcement">Announcement</option>
                    {activeTab === 'students' && <option value="fee_reminder">Fee Reminder</option>}
                    {activeTab === 'students' && <option value="result">Result</option>}
                    {activeTab === 'students' && <option value="library">Library</option>}
                    {activeTab === 'staff' && <option value="meeting">Meeting</option>}
                    {activeTab === 'staff' && <option value="holiday">Holiday</option>}
                    {activeTab === 'staff' && <option value="policy">Policy Update</option>}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <select
                    id="priority"
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              {/* Student Target Options */}
              {activeTab === 'students' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="target">Send To</Label>
                    <select
                      id="target"
                      value={formData.target_type}
                      onChange={(e) => setFormData({...formData, target_type: e.target.value, target_course_id: '', target_student_ids: []})}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="all">All Students</option>
                      <option value="course">Specific Course</option>
                      <option value="individual">Individual Student</option>
                    </select>
                  </div>

                  {formData.target_type === 'course' && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="course">Select Course</Label>
                        <select
                          id="course"
                          value={formData.target_course_id}
                          onChange={(e) => setFormData({...formData, target_course_id: e.target.value})}
                          className="w-full px-3 py-2 border rounded-md"
                        >
                          <option value="">-- Select Course --</option>
                          {courses.map(course => (
                            <option key={course.id} value={course.id}>{course.name}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="session">Select Session</Label>
                        <select
                          id="session"
                          value={formData.target_session_id}
                          onChange={(e) => setFormData({...formData, target_session_id: e.target.value})}
                          className="w-full px-3 py-2 border rounded-md"
                        >
                          <option value="">-- Select Session --</option>
                          {filterSessionsByCourse(sessions, formData.target_course_id).map(session => (
                            <option key={session.id} value={session.id}>{session.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {formData.target_type === 'individual' && (
                    <div className="space-y-2">
                      <Label htmlFor="student">Select Student</Label>
                      <select
                        id="student"
                        value={formData.target_student_ids[0] || ''}
                        onChange={(e) => setFormData({...formData, target_student_ids: [e.target.value]})}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="">-- Select Student --</option>
                        {students.map(student => (
                          <option key={student.id} value={student.student_id || student.id}>
                            {student.full_name} ({student.student_id || student.admission_number || student.phone})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              {/* Staff Target Options */}
              {activeTab === 'staff' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="staff_target">Send To</Label>
                    <select
                      id="staff_target"
                      value={formData.target_type}
                      onChange={(e) => setFormData({...formData, target_type: e.target.value, target_staff_ids: []})}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="all_staff">All Staff</option>
                      <option value="staff_individual">Individual Staff</option>
                    </select>
                  </div>

                  {formData.target_type === 'staff_individual' && (
                    <div className="space-y-2">
                      <Label htmlFor="staff_select">Select Staff</Label>
                      <select
                        id="staff_select"
                        value={formData.target_staff_ids[0] || ''}
                        onChange={(e) => setFormData({...formData, target_staff_ids: [e.target.value]})}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="">-- Select Staff --</option>
                        {staffList.map(staff => (
                          <option key={staff.id} value={staff.id}>
                            {staff.full_name} ({staff.employee_id}) - {staff.designation || staff.staff_type}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              <Button type="submit" className="w-full" disabled={isSending}>
                {isSending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send Notification</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Sent Notifications */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Notifications</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : notifications.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No notifications sent yet</p>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {notifications.map((notif) => (
                  <div key={notif.id} className="p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {getTargetIcon(notif.target_type)}
                          <span className="font-medium">{notif.title}</span>
                          {getPriorityBadge(notif.priority)}
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            notif.target_type?.includes('staff') ? 'bg-indigo-100 text-indigo-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {getTargetLabel(notif.target_type)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{notif.message}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          <span>{formatDate(notif.created_at)}</span>
                          <span>•</span>
                          <span>{notif.sent_by}</span>
                          {notif.auto_generated && (
                            <>
                              <span>•</span>
                              <span className="text-orange-500">Auto</span>
                            </>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(notif.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
